import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter, Tooltip } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { StoreValue } from 'effector';
import { useUnit } from 'effector-react';
import { FileSearch, HelpCircle } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TIME_ZONES } from 'shared/config';
import { translated } from 'shared/lib';
import { accountRoute, cashdeskRoute, userRoute } from 'shared/routing';
import {
	createDateFilter,
	createRegistry,
	MultiSelect,
	RegistryRootProps,
	RegistryTableTfootProps,
} from 'shared/ui';
import { ShiftReportRegistryModel } from '../../model';
import cls from './shift-report-registry.module.scss';

export type ShiftReportRegistryProps = RegistryRootProps & {
	emptyActionSlot?: ReactNode;
	topBarActionSlot?: ReactNode;
};

export const createShiftReportRegistry = ({
	registryModel,
	dateFilterModel,
	...model
}: ShiftReportRegistryModel) => {
	const DateFilter = createDateFilter(dateFilterModel);

	const DateField = reflect({
		view: DateFilter.DateField,
		bind: {
			'data-test-id': 'shift-report-registry-date-input',
			className: cls.shiftReportRegistryDate,
		},
	});

	const TimeZoneField = reflect({
		view: DateFilter.TimeZoneField,
		bind: {
			'data-test-id': 'shift-report-registry-time-zone-filter',
			className: cls.shiftReportRegistryTimeZone,
			value: registryModel.filters.timeZone.$value.map((tz) => tz.toString()),
			onChange: (value) =>
				registryModel.filters.timeZone.setValue(Number(value ?? TIME_ZONES['UTC-00:00'])),
		},
	});

	const CurrenciesField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'shift-report-registry-currencies-filter',
			value: registryModel.filters.currencies.$value,
			onChange: registryModel.filters.currencies.setValue,
			className: cls.shiftReportRegistryMultiSelect,
			data: model.$currencies,
			label: translated('shift-report-registry.filters.currencies.label'),
			placeholder: translated('shift-report-registry.filters.currencies.placeholder'),
			nothingFoundMessage: translated('shift-report-registry.filters.currencies.empty'),
			searchable: true,
		},
	});

	const CashiersField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'shift-report-registry-cashiers-filter',
			value: registryModel.filters.cashierIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => registryModel.filters.cashierIds.setValue(value.map(Number)),
			className: cls.shiftReportRegistryMultiSelect,
			data: model.$cashiers,
			label: translated('shift-report-registry.filters.cashiers.label'),
			placeholder: translated('shift-report-registry.filters.cashiers.placeholder'),
			nothingFoundMessage: translated('shift-report-registry.filters.cashiers.empty'),
		},
	});

	const CashdesksField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'shift-report-registry-cashdesks-filter',
			value: registryModel.filters.cashdeskIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => registryModel.filters.cashdeskIds.setValue(value.map(Number)),
			className: cls.shiftReportRegistryMultiSelect,
			data: model.$cashdesks,
			label: translated('shift-report-registry.filters.cashdesks.label'),
			placeholder: translated('shift-report-registry.filters.cashdesks.placeholder'),
			nothingFoundMessage: translated('shift-report-registry.filters.cashdesks.empty'),
		},
	});

	const AccountLink = reflect({
		view: Anchor<typeof Link>,
		bind: {
			'data-test-id': 'shift-report-registry-account-link',
			to: accountRoute,
			component: Link,
		},
	});

	const CashdeskLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof cashdeskRoute.$params>>>,
		bind: {
			'data-test-id': 'shift-report-registry-cashdesk-link',
			to: cashdeskRoute,
			component: Link,
		},
	});

	const CashierLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof userRoute.$params>>>,
		bind: {
			'data-test-id': 'shift-report-registry-cashier-link',
			to: userRoute,
			component: Link,
		},
	});

	const Registry = createRegistry(registryModel);

	const ShiftReportRegistryTotal = forwardRef<HTMLTableSectionElement, RegistryTableTfootProps>(
		(props, ref) => {
			const { t } = useTranslation();
			const [total, isMultiCurrencies] = useUnit([model.$total, model.$isMultiCurrencies]);

			if (!total) return null;

			return (
				<Registry.Table.Tfoot
					ref={ref}
					{...props}
				>
					<Registry.Table.Tr>
						<Registry.Table.Td colSpan={5}>{t('shift-report-registry.total')}</Registry.Table.Td>
						<Registry.Table.Td colSpan={3}>
							{!isMultiCurrencies && total.currency}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.deposits} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.withdrawals} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.jackpots} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.bonuses} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.income} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.incomeAllSources} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							<NumberFormatter
								value={total.rtp}
								suffix=' %'
							/>
						</Registry.Table.Td>
						<Registry.Table.Td>
							<NumberFormatter
								value={total.rtpAllSources}
								suffix=' %'
							/>
						</Registry.Table.Td>
						<Registry.Table.Td>
							<NumberFormatter
								value={total.numberOfBets}
								decimalScale={0}
							/>
						</Registry.Table.Td>
					</Registry.Table.Tr>
				</Registry.Table.Tfoot>
			);
		},
	);

	const ShiftReportRegistry = forwardRef<HTMLDivElement, ShiftReportRegistryProps>(
		({ topBarActionSlot, emptyActionSlot, ...props }, ref) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					<Registry.Title>{t('shift-report-registry.title')}</Registry.Title>
					<Registry.TopBar
						alwaysShow
						className={cls.shiftReportRegistryTopBar}
					>
						<Registry.Filters
							visibleFrom='md'
							classNames={{
								root: cls.shiftReportRegistryFilters,
								inner: cls.shiftReportRegistryFiltersInner,
							}}
						>
							<div className={cls.shiftReportRegistryFiltersGroup}>
								<DateField />
								<TimeZoneField />
							</div>
							<div className={cls.shiftReportRegistryFiltersGroup}>
								<CashiersField />
								<CashdesksField />
								<CurrenciesField />
							</div>
						</Registry.Filters>
						<div className={cls.shiftReportRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col className={cls.shiftReportRegistryColDate} />
							<Registry.Table.Col className={cls.shiftReportRegistryColDate} />
							<Registry.Table.Col className={cls.shiftReportRegistryColCurrency} />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col className={cls.shiftReportRegistryColRtp} />
							<Registry.Table.Col className={cls.shiftReportRegistryColRtp} />
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.cashier')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.cashdesk')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.account')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.start-time')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.end-time')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.currency')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.balance-open')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.balance-close')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.deposits')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.withdrawals')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.jackpots')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.bonuses')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.income')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.income-all-sources')}{' '}
									<Tooltip
										label={t('shift-report-registry.table-columns.income-all-sources-tooltip')}
									>
										<HelpCircle />
									</Tooltip>
								</Registry.Table.Th>
								<Registry.Table.Th>
									{`${t('shift-report-registry.table-columns.rtp')}`}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.rtp-all-sources')}{' '}
									<Tooltip label={t('shift-report-registry.table-columns.rtp-all-sources-tooltip')}>
										<HelpCircle />
									</Tooltip>
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('shift-report-registry.table-columns.number-of-bets')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList>
								{(row) => (
									<>
										<Registry.Table.Td>
											<CashierLink params={{ id: row.cashier.id }}>{row.cashier.name}</CashierLink>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<CashdeskLink params={{ id: row.cashdesk.id }}>
												{row.cashdesk.name}
											</CashdeskLink>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<AccountLink query={{ id: row.account.id }}>{row.account.name}</AccountLink>
										</Registry.Table.Td>
										<Registry.Table.Td>{row.startTime}</Registry.Table.Td>
										<Registry.Table.Td>{row.endTime}</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.openingBalance} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.closingBalance} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.deposits} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.withdrawals} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.jackpots} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.bonuses} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.income} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.incomeAllSources} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter
												value={(row.rtp * 100).toFixed(2)}
												suffix=' %'
											/>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter
												value={(row.rtpAllSources * 100).toFixed(2)}
												suffix=' %'
											/>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter
												value={row.numberOfBets}
												decimalScale={0}
											/>
										</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
						<ShiftReportRegistryTotal />
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('shift-report-registry.empty.title')}
						message={t('shift-report-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						withClearFiltersButton={false}
						icon={<FileSearch />}
						title={t('shift-report-registry.table-empty.title')}
						message={t('shift-report-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return ShiftReportRegistry;
};
